import React, { useState } from "react";
import { X, User, Users, Smartphone, Gift, Lock, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSnapshot } from "valtio";
import { userStore } from "../../hooks/store";
import { UserWeddingRole } from "../../types/wedding";
import { ClientStorage, ClientStorageItem } from "../../utils/clientstorage";
import { SettingsStore } from "../../utils/settings";
import InstallWeddn from "./install-weddn";
import { logout } from "../../utils/logout";

interface MoreOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

const MoreOverlay: React.FC<MoreOverlayProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { data: userData } = useSnapshot(userStore);
  const [showInstallWeddn, setShowInstallWeddn] = useState(false);

  const userWeddingRole = ClientStorage.fetch(
    ClientStorageItem.UserWeddingRole
  ) as UserWeddingRole;

  const getInitial = () => {
    if (userData?.firstname) {
      return userData.firstname.charAt(0).toUpperCase();
    }
    if (userData?.businessName) {
      return userData.businessName.charAt(0).toUpperCase();
    }
    return "U";
  };

  const getDisplayName = () => {
    if (userData?.firstname && userData?.lastname) {
      return `${userData.firstname} ${userData.lastname}`;
    }
    if (userData?.businessName) {
      return userData.businessName;
    }
    return "User";
  };

  const getRoleLabel = () => {
    if (userWeddingRole === UserWeddingRole.Bride) return "Bride";
    if (userWeddingRole === UserWeddingRole.Groom) return "Groom";
    if (userWeddingRole === UserWeddingRole.EventPlanner)
      return "Event Planner";
    return "User";
  };

  const menuItems = [
    {
      icon: User,
      label: "Edit profile",
      onClick: () => {
        navigate("/settings");
        onClose();
      },
    },
    {
      icon: Users,
      label: "Groom, planner & guest manager",
      onClick: () => {
        navigate("/settings/collaboration");
        onClose();
      },
    },
    {
      icon: Smartphone,
      label: "Guests check-in app",
      onClick: () => {
        navigate("/settings/check-in");
        onClose();
      },
    },
    {
      icon: Gift,
      label: "Wishlist gifting app",
      onClick: () => {
        navigate("/settings/wishlist");
        onClose();
      },
    },
    {
      icon: Lock,
      label: "Change password",
      onClick: () => {
        onClose();
        navigate("/settings");
        setTimeout(() => {
          SettingsStore.isPasswordDrawerOpen = true;
        }, 100);
      },
    },
    {
      icon: Smartphone,
      label: "Install weddn on your phone",
      onClick: () => {
        setShowInstallWeddn(true);
      },
    },
  ];

  if (!isOpen) return null;

  // Show InstallWeddn component if active
  if (showInstallWeddn) {
    return <InstallWeddn onBack={() => setShowInstallWeddn(false)} />;
  }

  return (
    <div className="fixed inset-0 z-[100] bg-white font-[glacial]">
      {/* Header */}
      <div className="flex items-start justify-between p-6 border-b border-gray-200">
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <div className="w-[80px] h-[80px] rounded-full bg-[#9B7B9E] flex items-center justify-center">
            <span className="text-white text-[24px] dcalligraphy italic">
              {getInitial()}
            </span>
          </div>
          {/* User Info */}
          <div>
            <h2 className="text-xl glacial text-gray-900">
              {getDisplayName()}
            </h2>
            <p className="text-sm glacial text-gray-500 mt-1">
              {getRoleLabel()}
            </p>
          </div>
        </div>
        {/* Close Button */}
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X size={24} className="text-gray-600" />
        </button>
      </div>

      {/* Menu Items */}
      <div className="py-4">
        {menuItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <button
              key={index}
              onClick={item.onClick}
              className="w-full flex items-center gap-4 px-6 py-6 hover:bg-gray-50 transition-colors text-left"
            >
              <Icon size={24} className="text-gray-700" strokeWidth={1.5} />
              <span className="text-base glacial text-gray-900">
                {item.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Logout Button */}
      <div className="absolute bottom-8 left-0 right-0 px-6">
        <button
          onClick={logout}
          className="w-full flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors text-left"
        >
          <LogOut size={24} className="text-red-500" strokeWidth={1.5} />
          <span className="text-base text-red-500">Log out</span>
        </button>
      </div>
    </div>
  );
};

export default MoreOverlay;
